import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TEST_IDS } from "@/lib/testIds";
import {
  Wallet, Plus, Trash2, ClipboardPaste, Save, Loader2, CalendarClock, TrendingUp, TrendingDown, RefreshCw, Sparkles, Bell, BellOff, Link2, Zap, DollarSign, FolderKanban, Pencil, Download, Unlink,
} from "lucide-react";
import usePushNotifications from "@/hooks/usePushNotifications";
import usePageSeo from "@/hooks/usePageSeo";
import TickerTape from "@/components/TickerTape";
import PortfolioValueChart from "@/components/holdings/PortfolioValueChart";
import DividendCalendar from "@/components/holdings/DividendCalendar";
import MonthlyIncomeChart from "@/components/holdings/MonthlyIncomeChart";
import ExDivAlertPreferences from "@/components/holdings/ExDivAlertPreferences";
import HoldingsOptimizerCard from "@/components/holdings/HoldingsOptimizerCard";
import IncomeGoalProgress from "@/components/holdings/IncomeGoalProgress";
import { exportHoldingsCsv } from "@/lib/exportHoldings";

const ACCOUNT_TYPES = ["Taxable", "Roth IRA", "Traditional IRA", "401k", "HSA", "Other"];

const fmt$ = (v) => v == null || v === "" ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt$0 = (v) => v == null ? "—" : `$${Math.round(Number(v)).toLocaleString()}`;
const fmtPct = (v) => v == null ? "—" : `${Number(v).toFixed(2)}%`;
const fmtNum = (v, d = 4) => v == null ? "—" : Number(v).toFixed(d);

// Cadence tone map — each rhythm gets a distinct accent so users can scan a
// mixed-cadence portfolio at a glance. Weekly YieldMax funds pop amber
// (highest-attention rhythm because they compound rebalance decisions
// weekly), monthly = emerald (income-lover favorite), quarterly = neutral
// primary tint, semi/annual = muted so they fade into the background.
const CADENCE_TONE = {
  "Weekly":       "bg-secondary/15 text-secondary border-secondary/40",
  "Bi-weekly":    "bg-secondary/10 text-secondary border-secondary/30",
  "Monthly":      "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  "Bi-monthly":   "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  "Quarterly":    "bg-primary/10 text-primary border-primary/30",
  "Semi-annual":  "bg-muted text-muted-foreground border-border",
  "Annual":       "bg-muted text-muted-foreground border-border",
};

// Compact "rhythm at a glance" tag rendered next to a ticker symbol. Renders
// nothing when the cadence is unknown (e.g. brand-new ticker with < 2
// historical divs, or a special-dividend-only payer where the median
// interval failed the 5-400 day sanity clamp in the backend).
function CadenceChip({ cadence }) {
  if (!cadence) return null;
  const tone = CADENCE_TONE[cadence] || "bg-muted text-muted-foreground border-border";
  return (
    <Badge
      variant="outline"
      className={`mono text-[9px] uppercase tracking-wider font-normal whitespace-nowrap ${tone}`}
      data-testid={`cadence-${cadence.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      {cadence}
    </Badge>
  );
}


export default function Holdings() {
  const nav = useNavigate();
  usePageSeo({
    title: "My Holdings — track dividend income live · Dividend Portfolio Planner",
    description: "Track real dividend holdings across Roth, Taxable, 401k, IRA, and HSA. Live prices, ex-div calendar, income chart, and CSV export.",
    path: "/holdings",
    noindex: true,
  });
  const [accounts, setAccounts] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  // "__all__" (default), "<portfolio_id>", or "__unassigned__". Filters the account tabs.
  const [portfolioFilter, setPortfolioFilter] = useState("__all__");
  const [portfolioMgrOpen, setPortfolioMgrOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [positions, setPositions] = useState([]);  // editable draft: [{ticker, shares, cost_basis}]
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [newAcctOpen, setNewAcctOpen] = useState(false);

  const filteredAccounts = useMemo(() => {
    if (portfolioFilter === "__all__") return accounts;
    if (portfolioFilter === "__unassigned__") return accounts.filter((a) => !a.portfolio_id);
    return accounts.filter((a) => a.portfolio_id === portfolioFilter);
  }, [accounts, portfolioFilter]);

  const loadAccounts = useCallback(async (selectId) => {
    setLoading(true);
    try {
      const [r, rp] = await Promise.all([
        api.get("/holdings/accounts"),
        api.get("/holdings/portfolios").catch(() => ({ data: { portfolios: [] } })),
      ]);
      const list = r.data.accounts || [];
      setAccounts(list);
      setPortfolios(rp.data.portfolios || []);
      setActiveId((prev) => {
        if (list.length === 0) return null;
        if (selectId && (selectId === "__all__" || list.find((a) => a.id === selectId))) return selectId;
        if (prev && (prev === "__all__" || list.find((a) => a.id === prev))) return prev;
        // 2+ accounts → land on the rollup view; single-account users get their one account.
        return list.length > 1 ? "__all__" : list[0].id;
      });
    } catch {
      toast.error("Could not load accounts");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // When the portfolio filter changes and the currently-active tab drops out of scope,
  // snap to the rollup for that new filter.
  useEffect(() => {
    if (activeId && activeId !== "__all__" && !filteredAccounts.find((a) => a.id === activeId)) {
      setActiveId(filteredAccounts.length > 0 ? "__all__" : null);
    }
  }, [portfolioFilter, filteredAccounts, activeId]);

  // When active account changes, reset draft positions from the stored doc.
  useEffect(() => {
    const acct = accounts.find((a) => a.id === activeId);
    if (acct) {
      setPositions((acct.positions || []).map((p) => ({ ...p })));
      setDirty(false);
    } else {
      setPositions([]);
      setLive(null);
    }
  }, [activeId, accounts]);

  const fetchLive = useCallback(async (id) => {
    if (!id) return;
    setLiveLoading(true);
    try {
      // Sentinel "__all__" → aggregated rollup across every account (respecting the portfolio filter).
      let url;
      if (id === "__all__") {
        const scope = portfolioFilter === "__all__" ? "" : `?portfolio_id=${encodeURIComponent(portfolioFilter)}`;
        url = `/holdings/summary${scope}`;
      } else {
        url = `/holdings/accounts/${id}/live`;
      }
      const r = await api.get(url);
      setLive(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load live data");
    } finally { setLiveLoading(false); }
  }, [portfolioFilter]);

  useEffect(() => { if (activeId) fetchLive(activeId); }, [activeId, fetchLive]);

  const activeAccount = accounts.find((a) => a.id === activeId);

  // --- Editing helpers ---
  const addPositionRow = () => {
    setPositions((p) => [...p, { ticker: "", shares: "", cost_basis: "" }]);
    setDirty(true);
  };
  const updatePosition = (i, field, val) => {
    setPositions((p) => p.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
    setDirty(true);
  };
  const removePosition = (i) => {
    setPositions((p) => p.filter((_, idx) => idx !== i));
    setDirty(true);
  };

  const savePositions = async () => {
    if (!activeId) return;
    // Validate: dedupe by ticker, drop empty rows.
    const cleaned = [];
    const seen = new Set();
    for (const row of positions) {
      const t = String(row.ticker || "").toUpperCase().trim();
      const s = Number(row.shares);
      const c = Number(row.cost_basis || 0);
      if (!t) continue;
      if (!s || s <= 0) { toast.error(`${t}: shares must be > 0`); return; }
      if (c < 0) { toast.error(`${t}: cost basis cannot be negative`); return; }
      if (seen.has(t)) { toast.error(`${t} appears twice — merge into one row`); return; }
      seen.add(t);
      cleaned.push({ ticker: t, shares: s, cost_basis: c });
    }
    setSaving(true);
    try {
      // Splice the fresh account into local state directly from the PATCH
      // response instead of triggering a full loadAccounts() reload. The
      // reload path was flipping `loading=true` mid-cycle and racing with
      // the `activeId` guard-useEffect, which would blank the account
      // editor for several seconds after every save (2026-02-14 bug).
      const r = await api.patch(`/holdings/accounts/${activeId}`, { positions: cleaned });
      const fresh = r.data;
      setAccounts((prev) => prev.map((a) => a.id === activeId ? fresh : a));
      toast.success("Positions saved");
      setDirty(false);
      // Refresh live prices for the newly-saved account so the value/gain
      // columns populate on the freshly-added rows.
      await fetchLive(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteAccount = async () => {
    if (!activeId || !activeAccount) return;
    if (!window.confirm(`Delete "${activeAccount.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/holdings/accounts/${activeId}`);
      toast.success("Account deleted");
      setActiveId(null);
      await loadAccounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <>
      <TickerTape />
      <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Ongoing income tracker</div>
          <h1 className="serif text-4xl sm:text-5xl mt-2">My holdings</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Paste your real brokerage positions across every account. DPP layers live prices + forward dividend income on top so you can see your monthly income at a glance.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PushToggle />
        </div>
      </div>

      {loading ? (
        <div className="h-40 rounded-lg bg-muted animate-pulse" />
      ) : accounts.length === 0 ? (
        <>
          <AccountTabs accounts={[]} activeId={null} onSelect={() => {}} allowRollup={false} portfolios={portfolios} onNewManual={() => setNewAcctOpen(true)} />
          <EmptyState onCreate={() => setNewAcctOpen(true)} />
        </>
      ) : (
        <>
          <IncomeGoalProgress className="mb-6" testId="holdings-income-goal" />
          <PortfolioFilterBar
            portfolios={portfolios}
            accounts={accounts}
            value={portfolioFilter}
            onChange={(v) => { if (dirty && !window.confirm("Discard unsaved changes?")) return; setPortfolioFilter(v); }}
            onManage={() => setPortfolioMgrOpen(true)}
          />

          <AccountTabs
            accounts={filteredAccounts}
            allowRollup={filteredAccounts.length > 1 || portfolioFilter !== "__all__"}
            portfolios={portfolios}
            activeId={activeId}
            onSelect={(id) => { if (dirty && !window.confirm("Discard unsaved changes?")) return; setActiveId(id); }}
            onNewManual={() => setNewAcctOpen(true)}
          />

          {activeId === "__all__" && (
            <RollupView
              live={live}
              loading={liveLoading}
              onRefresh={() => fetchLive("__all__")}
              portfolioScope={portfolioFilter}
              portfolioFilter={portfolioFilter}
              accounts={accounts}
              scopeLabel={
                portfolioFilter === "__all__" ? null :
                portfolioFilter === "__unassigned__" ? "Ungrouped" :
                (portfolios.find((p) => p.id === portfolioFilter)?.name || "Portfolio")
              }
            />
          )}

          {activeId !== "__all__" && activeAccount && (
            <>
              <SummaryCards live={live} loading={liveLoading} onRefresh={() => fetchLive(activeId)} portfolioFilter={portfolioFilter} activeAccountId={activeId} activeAccount={activeAccount} />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                <div className="lg:col-span-2 space-y-6">
                  <PositionsCard
                    positions={positions}
                    live={live}
                    dirty={dirty}
                    saving={saving}
                    onAdd={addPositionRow}
                    onUpdate={updatePosition}
                    onRemove={removePosition}
                    onSave={savePositions}
                    onPaste={() => setPasteOpen(true)}
                    onDeleteAccount={deleteAccount}
                    accountName={activeAccount.name}
                  />
                  <PortfolioValueChart portfolioId={null} />
                  <MonthlyIncomeChart portfolioId={null} />
                  <HoldingsOptimizerCard accounts={accounts} defaultScope={accounts.length > 1 ? activeId : "all"} />
                </div>
                <div className="space-y-6">
                  <DividendCalendar portfolioId={null} />
                  <ExDivCalendarCard events={live?.upcoming_ex_dividends || []} loading={liveLoading} />
                  <IncomeSummaryCard totals={live?.totals} />
                </div>
              </div>
            </>
          )}
        </>
      )}

      <NewAccountDialog
        open={newAcctOpen}
        onOpenChange={setNewAcctOpen}
        portfolios={portfolios}
        defaultPortfolioId={portfolioFilter !== "__all__" && portfolioFilter !== "__unassigned__" ? portfolioFilter : ""}
        onCreated={(id) => { setNewAcctOpen(false); loadAccounts(id); }}
      />
      <PasteDialog
        open={pasteOpen}
        onOpenChange={setPasteOpen}
        accountId={activeId}
        onApplied={async () => { setPasteOpen(false); await loadAccounts(activeId); await fetchLive(activeId); }}
      />
      <PortfolioManagerDialog
        open={portfolioMgrOpen}
        onOpenChange={setPortfolioMgrOpen}
        portfolios={portfolios}
        accounts={accounts}
        onChanged={async () => { await loadAccounts(activeId); }}
      />
    </div>
    </>
  );
}

// -------- Portfolio filter bar (multi-portfolio grouping) --------
function PortfolioFilterBar({ portfolios, accounts, value, onChange, onManage }) {
  const unassignedCount = accounts.filter((a) => !a.portfolio_id).length;
  return (
    <div className="mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="portfolio-filter-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Portfolio</span>
        <FilterChip active={value === "__all__"} onClick={() => onChange("__all__")} testId="pf-all">
          <FolderKanban className="w-3 h-3 mr-1.5" /> All ({accounts.length})
        </FilterChip>
        {portfolios.map((p) => (
          <FilterChip
            key={p.id}
            active={value === p.id}
            onClick={() => onChange(p.id)}
            testId={`pf-${p.id}`}
          >
            {p.name}
            <span className="ml-1.5 mono text-[9px] opacity-60">{p.account_count || 0}</span>
          </FilterChip>
        ))}
        {unassignedCount > 0 && (
          <FilterChip active={value === "__unassigned__"} onClick={() => onChange("__unassigned__")} testId="pf-unassigned">
            Ungrouped
            <span className="ml-1.5 mono text-[9px] opacity-60">{unassignedCount}</span>
          </FilterChip>
        )}
      </div>
      <Button variant="ghost" size="sm" onClick={onManage} data-testid="portfolios-manage-btn" className="text-muted-foreground">
        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Manage portfolios
      </Button>
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center px-3 py-1.5 rounded-full border text-xs transition-colors ${
        active ? "bg-secondary text-secondary-foreground border-secondary" : "bg-card border-border hover:border-secondary/40"
      }`}
    >
      {children}
    </button>
  );
}

// -------- Empty state --------
function EmptyState({ onCreate }) {
  return (
    <Card className="border-dashed">
      <CardContent className="p-12 text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto">
          <Wallet className="w-6 h-6" />
        </div>
        <h2 className="serif text-3xl mt-4">Start tracking your real income.</h2>
        <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
          Create your first account (Roth, Taxable, 401k) and paste your positions. DPP does the math — no spreadsheet needed.
        </p>
        <Button className="mt-6 rounded-full" onClick={onCreate} data-testid="holdings-empty-create">
          <Plus className="w-4 h-4 mr-2" /> Create first account
        </Button>
      </CardContent>
    </Card>
  );
}

// -------- Account tabs: brokerage-synced and manual accounts, each row with its own actions --------
const tabCls = (on) => `px-4 py-2 rounded-full border text-sm transition-colors ${
  on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"}`;

function AccountTabs({ accounts, activeId, onSelect, allowRollup, portfolios, onNewManual }) {
  const totalPositions = accounts.reduce((s, a) => s + (a.positions?.length || 0), 0);
  const portfolioNameById = useMemo(() => {
    const m = {};
    (portfolios || []).forEach((p) => { m[p.id] = p.name; });
    return m;
  }, [portfolios]);
  const synced = accounts.filter((a) => a.provider === "snaptrade");
  const manual = accounts.filter((a) => a.provider !== "snaptrade");
  const tab = (a) => (
    <button key={a.id} type="button" onClick={() => onSelect(a.id)} data-testid={`holdings-tab-${a.id}`} className={`${tabCls(a.id === activeId)} inline-flex items-center`}>
      {a.provider === "snaptrade" && <BrokerBadge name={a.broker_name} logo={a.broker_logo_url} testId={`holdings-tab-broker-${a.id}`} />}
      <span className="mono text-[10px] uppercase tracking-widest opacity-70 mr-2">{a.type}</span>
      {a.name}
      {a.portfolio_id && portfolioNameById[a.portfolio_id] && (
        <span className="ml-2 mono text-[9px] uppercase opacity-60">· {portfolioNameById[a.portfolio_id]}</span>
      )}
      <span className="ml-2 mono text-[10px] opacity-60">{a.positions?.length || 0}</span>
    </button>
  );
  return (
    <div className="space-y-3 mb-6" data-testid={TEST_IDS.holdingsAccountTabs}>
      {allowRollup && (
        <button type="button" onClick={() => onSelect("__all__")} data-testid="holdings-tab-all" className={tabCls(activeId === "__all__")}>
          <span className="mono text-[10px] uppercase tracking-widest opacity-70 mr-2">Rollup</span>
          All accounts
          <span className="ml-2 mono text-[10px] opacity-60">{totalPositions}</span>
        </button>
      )}
      <AccountGroup
        testId="holdings-group-brokerage" icon={Link2} title="Brokerage accounts" hint="Read-only sync via SnapTrade"
        empty="No brokerage connected yet." actions={<BrokerageConnectButton />}
      >
        {synced.map(tab)}
      </AccountGroup>
      <AccountGroup
        testId="holdings-group-manual" icon={Pencil} title="Manual accounts" hint="Positions you type or paste"
        empty="No manual accounts yet."
        actions={
          <Button size="sm" variant="outline" className="rounded-full h-9" onClick={onNewManual} data-testid={TEST_IDS.holdingsAccountAdd}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New manual account
          </Button>
        }
      >
        {manual.map(tab)}
      </AccountGroup>
    </div>
  );
}

// Broker logo (from SnapTrade) or a letter badge, plus the broker's name.
function BrokerBadge({ name, logo, testId }) {
  const [broken, setBroken] = useState(false);
  const label = name || "Brokerage";
  return (
    <span className="inline-flex items-center gap-1.5 mr-2 pr-2 border-r border-border/70" data-testid={testId} title={label}>
      {logo && !broken
        ? <img src={logo} alt="" className="w-4 h-4 rounded-sm bg-white object-contain" onError={() => setBroken(true)} />
        : <span className="w-4 h-4 rounded-sm bg-secondary text-secondary-foreground grid place-items-center text-[9px] font-bold">{label[0].toUpperCase()}</span>}
      <span className="text-xs font-semibold max-w-[120px] truncate">{label}</span>
    </span>
  );
}

function AccountGroup({ testId, icon: Icon, title, hint, empty, actions, children }) {
  const has = React.Children.count(children) > 0;
  return (
    <section className="rounded-xl border border-border bg-card/60 p-4" data-testid={testId}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <Icon className="w-3.5 h-3.5 text-primary" />
            <h2 className="text-sm font-bold text-primary [font-family:inherit] tracking-normal whitespace-nowrap">{title}</h2>
            <span className="text-xs text-muted-foreground basis-full sm:basis-auto">{hint}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {has ? children : <span className="text-sm text-muted-foreground py-2" data-testid={`${testId}-empty`}>{empty}</span>}
          </div>
        </div>
        <div className="lg:max-w-[60%] shrink-0">{actions}</div>
      </div>
    </section>
  );
}

// -------- All-accounts Rollup view (read-only, aggregated) --------
function RollupView({ live, loading, onRefresh, portfolioScope, scopeLabel, portfolioFilter, accounts }) {
  const [expandedTicker, setExpandedTicker] = useState(null);
  const positions = live?.positions || [];
  const upcoming = live?.upcoming_ex_dividends || [];
  const chartPortfolioId = portfolioScope && portfolioScope !== "__all__" ? portfolioScope : null;

  return (
    <div data-testid="holdings-rollup-view">
      {scopeLabel && (
        <div className="mb-3 flex items-center gap-2">
          <Badge className="mono text-[10px] uppercase" variant="outline" data-testid="rollup-scope-badge">
            <FolderKanban className="w-3 h-3 mr-1" /> Filtered: {scopeLabel}
          </Badge>
          <span className="text-xs text-muted-foreground">Totals below cover only this scope.</span>
        </div>
      )}
      <SummaryCards live={live} loading={loading} onRefresh={onRefresh} portfolioFilter={portfolioFilter} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div>
                  <h2 className="serif text-2xl">All positions</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Same-ticker shares combined across every account. Click a row to see the per-account breakdown.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportHoldingsCsv({
                      positions,
                      totals: live?.totals,
                      scopeLabel: scopeLabel || "All accounts",
                    })}
                    disabled={loading || positions.length === 0}
                    className="rounded-full"
                    data-testid="rollup-export-csv"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} className="rounded-full">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                  </Button>
                </div>
              </div>

              {positions.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-10 border border-dashed border-border rounded-md">
                  Add positions to any account to see them rolled up here.
                </div>
              )}

              {positions.length > 0 && (
                <div className="overflow-hidden rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/60">
                      <tr className="text-left">
                        <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Ticker</th>
                        <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Shares</th>
                        <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Avg cost</th>
                        <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Price</th>
                        <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Value</th>
                        <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Gain</th>
                        <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Fwd/yr</th>
                        <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Yield</th>
                        <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Accounts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map((p) => {
                        const open = expandedTicker === p.ticker;
                        const gain = p.gain_abs;
                        const ba = p.by_account || [];
                        return (
                          <React.Fragment key={p.ticker}>
                            <tr
                              className={`border-t border-border cursor-pointer hover:bg-muted/30 ${open ? "bg-muted/40" : ""}`}
                              onClick={() => setExpandedTicker(open ? null : p.ticker)}
                              data-testid={`rollup-row-${p.ticker}`}
                            >
                              <td className="px-3 py-2 mono font-semibold">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span>{p.ticker}</span>
                                  <CadenceChip cadence={p.payout_cadence} />
                                </div>
                              </td>
                              <td className="px-3 py-2 mono text-xs text-right">{fmtNum(p.shares, 2)}</td>
                              <td className="px-3 py-2 mono text-xs text-right" data-testid={`rollup-avgcost-${p.ticker}`}>{fmt$(p.cost_basis_per_share)}</td>
                              <td className="px-3 py-2 mono text-xs text-right">{fmt$(p.current_price)}</td>
                              <td className="px-3 py-2 mono text-xs text-right">{fmt$0(p.current_value)}</td>
                              <td className={`px-3 py-2 mono text-xs text-right ${gain == null ? "" : (gain >= 0 ? "text-primary" : "text-destructive")}`}>
                                {gain == null ? "—" : `${gain >= 0 ? "+" : ""}${fmt$0(gain)}`}
                              </td>
                              <td className="px-3 py-2 mono text-xs text-right">{fmt$0(p.forward_annual_income)}</td>
                              <td className="px-3 py-2 mono text-xs text-right">{fmtPct(p.yield_pct)}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1 flex-wrap">
                                  {ba.slice(0, 3).map((b) => (
                                    <Badge key={b.account_id} variant="outline" className="mono text-[9px] uppercase whitespace-nowrap">
                                      {b.account_name}
                                    </Badge>
                                  ))}
                                  {ba.length > 3 && (
                                    <Badge variant="outline" className="mono text-[9px]">+{ba.length - 3}</Badge>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {open && (
                              <tr className="bg-muted/20">
                                <td colSpan={9} className="px-6 py-3">
                                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Per-account breakdown</div>
                                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {ba.map((b) => (
                                      <div key={b.account_id} className="rounded-md border border-border bg-background p-2.5">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{b.account_type}</span>
                                          <span className="mono text-xs font-semibold">{fmtNum(b.shares, 2)} sh</span>
                                        </div>
                                        <div className="text-sm font-medium truncate">{b.account_name}</div>
                                        <div className="mono text-[10px] text-muted-foreground mt-0.5">
                                          Cost basis {fmt$(b.cost_basis)}
                                          {b.shares > 0 && b.cost_basis > 0 && (
                                            <> · avg {fmt$(b.cost_basis / b.shares)}/sh</>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <PortfolioValueChart portfolioId={chartPortfolioId} />
          <MonthlyIncomeChart portfolioId={chartPortfolioId} />
          <HoldingsOptimizerCard accounts={accounts} />
        </div>

        <div className="space-y-6">
          <ExDivAlertPreferences />
          <DividendCalendar portfolioId={chartPortfolioId} />
          <ExDivCalendarCard events={upcoming} loading={loading} />
          <DividendsYtdCard />
          <IncomeSummaryCard totals={live?.totals} />
          <Card>
            <CardContent className="p-5">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Account mix</div>
              <div className="space-y-2">
                {(live?.accounts || []).map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{a.name}</div>
                      <div className="mono text-[10px] text-muted-foreground">{a.type}</div>
                    </div>
                    <Badge variant="outline" className="mono text-[10px]">{a.position_count} pos</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// -------- Summary metric cards --------
// Trailing 90-day RECEIVED income — sums yfinance historical `.dividends`
// series for every held ticker within the portfolio scope. Distinct from the
// "Est. monthly income" tile which projects FORWARD income based on annual
// dividend rate / cadence. Users get to see "what I actually collected in
// the last 90 days" side-by-side with "what I'm on track to collect".
// Auto-fit numeric display: shrinks font-size to fit the parent's width so
// long values like "$1,234,567.89" never truncate or wrap on the summary
// cards. Uses direct DOM font-size mutation (no state) inside a layout
// effect to avoid re-render loops when the parent width changes.
function AutoFitNumber({ children, className = "", maxPx = 30, minPx = 15 }) {
  const wrapRef = useRef(null);
  const textRef = useRef(null);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const text = textRef.current;
    if (!wrap || !text) return;
    const fit = () => {
      let s = maxPx;
      text.style.fontSize = `${s}px`;
      // Shrink 1px at a time until it fits or we hit the floor. Cheap and
      // deterministic — the summary card is ~180-260px wide so worst case
      // is ~15 iterations of a single scrollWidth read.
      while (s > minPx && text.scrollWidth > wrap.clientWidth) {
        s -= 1;
        text.style.fontSize = `${s}px`;
      }
    };
    fit();
    // Re-fit when the container resizes (viewport shrinks, sidebar toggles).
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [children, maxPx, minPx]);
  return (
    <div ref={wrapRef} className={`w-full overflow-hidden serif tabular-nums leading-tight ${className}`}>
      <span ref={textRef} className="whitespace-nowrap inline-block">{children}</span>
    </div>
  );
}


function TrailingIncomeCard({ portfolioFilter, accountId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { days: 90 };
    // Per-account narrowing takes precedence — matches the backend priority
    // in /api/holdings/trailing-income. When "__all__" is active we fall back
    // to the portfolio filter for the rollup.
    if (accountId && accountId !== "__all__") {
      params.account_id = accountId;
    } else if (portfolioFilter && portfolioFilter !== "__all__") {
      params.portfolio_id = portfolioFilter;
    }
    api.get("/holdings/trailing-income", { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioFilter, accountId]);

  const total = data?.total_received ?? 0;
  const events = data?.event_count ?? 0;
  const topTicker = (data?.by_ticker || [])[0];
  return (
    <Card data-testid="holdings-metric-trailing-income" className="card-hover">
      <CardContent className="p-5">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Received (last 90 days)
        </div>
        <div className="mt-2">
          {loading ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /> : <AutoFitNumber>{fmt$(total)}</AutoFitNumber>}
        </div>
        {!loading && (
          <div className="mono text-[10px] text-muted-foreground mt-1">
            {events === 0 ? "no payments yet" : (
              <>
                {events} payment{events === 1 ? "" : "s"}
                {topTicker && ` · top ${topTicker.ticker} ${fmt$(topTicker.amount)}`}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Live cash + buying-power tile from the linked brokerage (Schwab, Fidelity,
// etc.). Uses SnapTrade's balances endpoint — free, always available on any
// linked account. Silently hides when the user hasn't connected a broker so
// we don't clutter the dashboard with an empty tile.
function BrokerBalanceCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadBalances = React.useCallback(async () => {
    try {
      const r = await api.get("/snaptrade/balances");
      setData(r.data);
    } catch (_) { setData(null); }
  }, []);

  const triggerRefresh = React.useCallback(async () => {
    // Kicks off a background sync (SnapTrade already re-queries the broker
    // on every /sync — no separate refresh call needed like Plaid required),
    // polls /snaptrade/status for completion, then reloads the balance data.
    setRefreshing(true);
    try {
      await api.post("/snaptrade/sync", {});
      const started = Date.now();
      while (Date.now() - started < 120000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const s = await api.get("/snaptrade/status");
          const st = s.data?.item?.sync_state;
          if (st?.status === "success" || st?.status === "error") break;
        } catch (_) {}
      }
      await loadBalances();
    } finally { setRefreshing(false); }
  }, [loadBalances]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.get("/snaptrade/status");
        if (!s.data?.connected) { setVisible(false); return; }
        setVisible(true);
        await loadBalances();
      } catch (_) { if (!cancelled) setData(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [loadBalances]);

  if (!visible) return null;
  const cash = data?.cash_total ?? 0;
  const cashAccounts = (data?.accounts || []).filter((a) => a.is_cash);
  return (
    <Card data-testid="holdings-metric-broker-cash" className="card-hover">
      <CardContent className="p-5">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Cash at {data?.institution || "broker"}
        </div>
        <div className="mt-2">
          {loading ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /> : <AutoFitNumber>{fmt$(cash)}</AutoFitNumber>}
        </div>
        {!loading && (
          <>
            <div className="mono text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
              {(data?.accounts || []).length === 0 ? (
                <>no accounts</>
              ) : (
                <button
                  type="button"
                  className="underline decoration-dotted hover:text-foreground"
                  onClick={() => setExpanded((v) => !v)}
                  data-testid="broker-cash-toggle"
                >
                  ready to invest · {cashAccounts.length} account{cashAccounts.length === 1 ? "" : "s"} {expanded ? "▲" : "▼"}
                </button>
              )}
              {/* Manual refresh — useful when the tile shows $0 despite the user
                  knowing they have cash (usually means the balances_snapshot
                  hasn't been written yet post-deploy). */}
              <button
                type="button"
                onClick={triggerRefresh}
                disabled={refreshing}
                className="ml-auto text-[9px] uppercase tracking-widest hover:text-foreground disabled:opacity-50"
                data-testid="broker-cash-refresh"
                title="Force-refresh cash balances from your broker"
              >
                {refreshing ? "refreshing…" : "↻ refresh"}
              </button>
            </div>
            {expanded && (data?.accounts || []).length > 0 && (
              <div className="mt-2 space-y-1 text-xs" data-testid="broker-cash-breakdown">
                <div className="mono text-[9px] text-muted-foreground uppercase tracking-widest pb-1">
                  Account · cash · source
                </div>
                {(data?.accounts || []).map((a) => {
                  const included = a.is_cash;
                  return (
                    <div key={a.account_id} className={`flex items-start justify-between gap-2 border-t border-border/50 pt-1 ${included ? "" : "opacity-50"}`}>
                      <div className="min-w-0 flex-1">
                        <div className="truncate">
                          {a.name}{a.mask ? ` ····${a.mask}` : ""}
                        </div>
                        <div className="mono text-[9px] text-muted-foreground uppercase">
                          {a.type}/{a.subtype || "—"} {included ? `· ${a.cash_source}` : "· no cash"}
                        </div>
                      </div>
                      <div className="text-right mono shrink-0">
                        <div className="font-medium">{fmt$(a.cash ?? 0)}</div>
                        <div className="text-muted-foreground text-[10px]">
                          curr {fmt$(a.current)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}



function SummaryCards({ live, loading, onRefresh, portfolioFilter, activeAccountId, activeAccount }) {
  const t = live?.totals || {};
  // If viewing a single SnapTrade-synced account, pick the account-native
  // broker totals (SnapTrade contract, no React math). Otherwise fall back
  // to the rollup totals from `/api/holdings/summary`.
  const isSingleBrokerAcct = activeAccount && activeAccount.provider === "snaptrade" && activeAccount.snap_total_value != null;
  const positionsValue = isSingleBrokerAcct ? activeAccount.snap_positions_value : (t.total_value_ex_cash ?? t.total_value);
  const brokerCash = isSingleBrokerAcct ? activeAccount.snap_cash : Number(t.cash_at_broker || 0);
  const totalAccountValue = isSingleBrokerAcct ? activeAccount.snap_total_value : t.total_value;
  const buyingPower = isSingleBrokerAcct ? activeAccount.snap_buying_power : Number(t.buying_power_at_broker || 0);

  const cards = [
    {
      label: "Total account value",
      value: fmt$(totalAccountValue),
      sub: brokerCash > 0 ? `${fmt$(positionsValue)} positions + ${fmt$(brokerCash)} cash` : "",
      testId: TEST_IDS.holdingsMetricValue,
    },
    {
      label: "Positions value",
      value: fmt$(positionsValue),
      sub: isSingleBrokerAcct ? "from broker" : "",
      testId: "holdings-metric-positions-value",
    },
    {
      label: "Cash at broker",
      value: fmt$(brokerCash),
      sub: buyingPower > 0 && buyingPower !== brokerCash ? `${fmt$(buyingPower)} buying power` : (buyingPower > 0 ? "= buying power" : ""),
      testId: "holdings-metric-broker-cash",
    },
    { label: "Est. monthly income", value: fmt$(t.forward_monthly_income), testId: TEST_IDS.holdingsMetricMonthlyIncome },
    { label: "Yield on cost", value: fmtPct(t.yield_on_cost_pct), testId: TEST_IDS.holdingsMetricYoc },
    {
      label: "Unrealized gain",
      value: t.total_gain_abs == null ? "—" : `${t.total_gain_abs >= 0 ? "+" : ""}${fmt$(t.total_gain_abs)}`,
      sub: t.total_gain_pct == null ? "" : `${t.total_gain_pct >= 0 ? "+" : ""}${fmtPct(t.total_gain_pct)}`,
      tone: t.total_gain_abs == null ? "" : (t.total_gain_abs >= 0 ? "text-primary" : "text-destructive"),
      testId: TEST_IDS.holdingsMetricGain,
    },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
      {cards.map((c) => (
        <Card key={c.label} data-testid={c.testId} className="card-hover">
          <CardContent className="p-5">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</div>
            <div className={`mt-2 ${c.tone || ""}`}>
              {loading
                ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                : <AutoFitNumber>{c.value}</AutoFitNumber>}
            </div>
            {c.sub && <div className={`mono text-xs mt-1 ${c.tone}`}>{c.sub}</div>}
          </CardContent>
        </Card>
      ))}
      <TrailingIncomeCard portfolioFilter={portfolioFilter} accountId={activeAccountId} />
      <BrokerBalanceCard />
      <div className="sm:col-span-2 lg:col-span-6 flex items-center justify-end -mt-2">
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading} data-testid="holdings-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh live data
        </Button>
      </div>
    </div>
  );
}

// -------- Positions editor + live table --------
function PositionsCard({
  positions, live, dirty, saving, onAdd, onUpdate, onRemove, onSave, onPaste, onDeleteAccount, accountName,
}) {
  const livePositionsByTicker = useMemo(() => {
    const map = {};
    (live?.positions || []).forEach((p) => { map[p.ticker] = p; });
    return map;
  }, [live]);

  const exportCsv = () => {
    if (!live?.positions?.length) return;
    exportHoldingsCsv({
      positions: live.positions,
      totals: live.totals,
      scopeLabel: accountName || "Account",
    });
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="serif text-2xl">Positions in {accountName}</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!live?.positions?.length} data-testid="account-export-csv">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={onPaste} data-testid={TEST_IDS.holdingsPasteButton}>
              <ClipboardPaste className="w-4 h-4 mr-1.5" /> Paste positions
            </Button>
            <Button variant="outline" size="sm" onClick={onAdd} data-testid={TEST_IDS.holdingsAddPositionButton}>
              <Plus className="w-4 h-4 mr-1.5" /> Add row
            </Button>
            <Button
              size="sm"
              className="rounded-full"
              onClick={onSave}
              disabled={!dirty || saving}
              data-testid={TEST_IDS.holdingsSaveButton}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save
            </Button>
          </div>
        </div>

        {positions.length === 0 ? (
          <div className="text-center py-10 border border-dashed rounded-md">
            <Sparkles className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
            <div className="text-sm text-muted-foreground">
              No positions yet — <button onClick={onPaste} className="underline underline-offset-2 hover:text-primary">paste from your broker</button> or add rows manually.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={TEST_IDS.holdingsPositionsTable}>
              <thead>
                <tr className="text-left mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3">Ticker</th>
                  <th className="py-2 pr-3 text-right">Shares</th>
                  <th className="py-2 pr-3 text-right">Cost basis ($)</th>
                  <th className="py-2 pr-3 text-right">Avg cost</th>
                  <th className="py-2 pr-3 text-right">Price</th>
                  <th className="py-2 pr-3 text-right">Value</th>
                  <th className="py-2 pr-3 text-right">Gain</th>
                  <th className="py-2 pr-3 text-right">YoC</th>
                  <th className="py-2 pr-3 text-right">Fwd income</th>
                  <th className="py-2 pr-3 text-right">% port</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {positions.map((row, i) => {
                  const t = String(row.ticker || "").toUpperCase().trim();
                  const l = livePositionsByTicker[t];
                  const shares = Number(row.shares) || 0;
                  const cost = Number(row.cost_basis) || 0;
                  const avgCost = shares > 0 && cost > 0 ? cost / shares : (l?.cost_basis_per_share || null);
                  return (
                    <tr key={i} className="border-b border-border/60" data-testid={TEST_IDS.holdingsPositionRow}>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1.5">
                          <Input
                            value={row.ticker}
                            onChange={(e) => onUpdate(i, "ticker", e.target.value.toUpperCase())}
                            placeholder="SCHD"
                            className="h-8 w-24 mono uppercase"
                            data-testid={`holdings-ticker-${i}`}
                          />
                          <CadenceChip cadence={l?.payout_cadence} />
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <Input
                          type="number"
                          value={row.shares}
                          onChange={(e) => onUpdate(i, "shares", e.target.value)}
                          placeholder="0"
                          className="h-8 w-24 text-right mono"
                          step="0.0001"
                          data-testid={`holdings-shares-${i}`}
                        />
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <Input
                          type="number"
                          value={row.cost_basis}
                          onChange={(e) => onUpdate(i, "cost_basis", e.target.value)}
                          placeholder="0"
                          className="h-8 w-28 text-right mono"
                          step="0.01"
                          data-testid={`holdings-cost-${i}`}
                        />
                      </td>
                      <td className="py-2 pr-3 text-right mono text-xs text-muted-foreground" data-testid={`holdings-avgcost-${i}`}>
                        {avgCost ? fmt$(avgCost) : "—"}
                      </td>
                      <td className="py-2 pr-3 text-right mono">{l?.current_price ? `$${l.current_price}` : "—"}</td>
                      <td className="py-2 pr-3 text-right mono">{fmt$(l?.current_value)}</td>
                      <td className={`py-2 pr-3 text-right mono ${l?.gain_abs == null ? "" : l.gain_abs >= 0 ? "text-primary" : "text-destructive"}`}>
                        {l?.gain_pct == null ? "—" : `${l.gain_pct >= 0 ? "+" : ""}${fmtPct(l.gain_pct)}`}
                      </td>
                      <td className="py-2 pr-3 text-right mono text-secondary">{fmtPct(l?.yoc_pct)}</td>
                      <td className="py-2 pr-3 text-right mono">{fmt$0(l?.forward_annual_income)}</td>
                      <td className="py-2 pr-3 text-right mono">{fmtPct(l?.weight_pct)}</td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => onRemove(i)}
                          className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          aria-label={`Remove ${t || "row"}`}
                          data-testid={TEST_IDS.holdingsRemovePosition}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Live prices via Yahoo Finance · figures may lag by 15 min · not investment advice
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDeleteAccount}
            className="text-muted-foreground hover:text-destructive"
            data-testid="holdings-delete-account"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete account
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// -------- Ex-div calendar --------
function ExDivCalendarCard({ events, loading }) {
  return (
    <Card data-testid={TEST_IDS.holdingsExDivCalendar}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock className="w-4 h-4 text-secondary" />
          <h3 className="serif text-xl">Upcoming ex-dividends</h3>
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : events.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No ex-dividend dates in the next 120 days for these holdings.
          </div>
        ) : (
          <ul className="space-y-2">
            {events.slice(0, 10).map((ev, i) => (
              <li key={`${ev.ticker}-${i}`} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
                <div>
                  <div className="mono text-sm font-semibold">{ev.ticker}</div>
                  <div className="text-xs text-muted-foreground">
                    {ev.ex_date} · <span className={ev.days_until <= 7 ? "text-secondary font-medium" : ""}>
                      {ev.days_until <= 0 ? "today/past" : `in ${ev.days_until}d`}
                    </span>
                  </div>
                </div>
                {ev.estimated_payment > 0 && (
                  <div className="mono text-sm text-primary">≈ {fmt$(ev.estimated_payment)}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// -------- Income summary --------
function IncomeSummaryCard({ totals }) {
  const t = totals || {};
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="serif text-xl">Forward income</h3>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Row label="Annual" value={fmt$(t.forward_annual_income)} />
          <Row label="Monthly (avg)" value={fmt$(t.forward_monthly_income)} highlight />
          <Row label="TTM received" value={fmt$(t.ttm_dividends)} />
          <Row label="Current yield" value={fmtPct(t.current_yield_pct)} />
          <Row label="Positions" value={t.position_count ?? 0} />
          <Row label="Yield on cost" value={fmtPct(t.yield_on_cost_pct)} />
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mono ${highlight ? "text-primary text-base" : "text-sm"}`}>{value}</div>
    </div>
  );
}

// Real dividend cash from SnapTrade transactions — YTD sum. Renders 3 states:
// (1) loading, (2) available with real $ + top tickers, (3) unavailable
// (no brokerage linked, no synced history yet, or error).
function DividendsYtdCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // YTD = days since Jan 1 of current year. SnapTrade transactions
        // endpoint accepts a `days` filter — compute how many days back
        // to Jan 1 and query for DIVIDEND type only.
        const now = new Date();
        const jan1 = new Date(now.getFullYear(), 0, 1);
        const daysSinceJan1 = Math.max(1, Math.ceil((now - jan1) / 86400000));
        const r = await api.get("/snaptrade/transactions", {
          params: { days: daysSinceJan1, type: "DIVIDEND" },
        });
        if (!cancelled) {
          // Build the by_ticker rollup client-side so the UI still shows
          // top payers using the same free /transactions endpoint.
          const byTicker = {};
          (r.data.transactions || []).forEach((t) => {
            const tk = t.ticker || "—";
            byTicker[tk] = (byTicker[tk] || 0) + Number(t.amount || 0);
          });
          const rows = Object.entries(byTicker)
            .map(([ticker, amount]) => ({ ticker, amount }))
            .sort((a, b) => b.amount - a.amount);
          setData({
            available: (r.data.count || 0) > 0,
            ytd: r.data.total_amount || 0,
            count: r.data.count || 0,
            by_ticker: rows,
            reason: (r.data.count || 0) === 0 ? "no_data" : null,
          });
        }
      } catch {
        if (!cancelled) setData({ available: false, reason: "error", ytd: 0, count: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Card data-testid="dividends-ytd-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-4 h-4 text-primary" />
          <h3 className="serif text-xl">Dividends received {new Date().getFullYear()}</h3>
        </div>
        {loading && <div className="text-sm text-muted-foreground py-2">Loading…</div>}
        {!loading && data?.available && (
          <>
            <div className="serif text-3xl text-primary" data-testid="dividends-ytd-total">{fmt$(data.ytd)}</div>
            <div className="mono text-[10px] text-muted-foreground mt-1">{data.count} payment(s) · live from SnapTrade</div>
            {(data.by_ticker || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Top payers</div>
                {(data.by_ticker || []).slice(0, 5).map((r) => (
                  <div key={r.ticker} className="flex items-center justify-between text-sm">
                    <span className="mono font-semibold">{r.ticker}</span>
                    <span className="mono text-xs">{fmt$(r.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {!loading && !data?.available && (
          <div className="text-sm text-muted-foreground leading-relaxed" data-testid="dividends-ytd-unavailable">
            {data?.reason === "no_data" && (
              <>Tap <span className="mono text-primary">Sync history (2/mo)</span> above to pull your real dividend payments from SnapTrade.</>
            )}
            {data?.reason === "error" && (
              <>Reconnect your brokerage from the Connect button above.</>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -------- New account dialog --------
function NewAccountDialog({ open, onOpenChange, onCreated, portfolios = [], defaultPortfolioId = "" }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("Taxable");
  const [portfolioId, setPortfolioId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setType("Taxable");
      setPortfolioId(defaultPortfolioId || "");
    }
  }, [open, defaultPortfolioId]);

  const save = async () => {
    if (!name.trim()) { toast.error("Account name required"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), type, positions: [] };
      if (portfolioId) payload.portfolio_id = portfolioId;
      const r = await api.post("/holdings/accounts", payload);
      toast.success(`"${r.data.name}" created`);
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="holdings-new-account-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">New holdings account</DialogTitle>
          <DialogDescription>Give it a name and pick an account type. You can rename or delete it any time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Account name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Roth IRA @ Fidelity"
              data-testid="holdings-new-account-name"
            />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="holdings-new-account-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {portfolios.length > 0 && (
            <div>
              <Label>Portfolio (optional)</Label>
              <select
                value={portfolioId}
                onChange={(e) => setPortfolioId(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="holdings-new-account-portfolio"
              >
                <option value="">Ungrouped</option>
                {portfolios.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="holdings-new-account-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Portfolio manager dialog (create / rename / delete + assign accounts) --------
function PortfolioManagerDialog({ open, onOpenChange, portfolios, accounts, onChanged }) {
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  useEffect(() => { if (open) { setNewName(""); setNewDesc(""); setEditingId(null); } }, [open]);

  const create = async () => {
    if (!newName.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/holdings/portfolios", { name: newName.trim(), description: newDesc.trim() || undefined });
      toast.success(`Portfolio "${newName}" created`);
      setNewName(""); setNewDesc("");
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const rename = async (p) => {
    if (!editName.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.patch(`/holdings/portfolios/${p.id}`, { name: editName.trim() });
      toast.success("Renamed");
      setEditingId(null);
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete portfolio "${p.name}"? Its accounts become Ungrouped — no data is lost.`)) return;
    setBusy(true);
    try {
      await api.delete(`/holdings/portfolios/${p.id}`);
      toast.success("Portfolio deleted");
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const assignAccount = async (accountId, portfolioId) => {
    setBusy(true);
    try {
      await api.patch(`/holdings/accounts/${accountId}`, { portfolio_id: portfolioId || "" });
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="portfolio-manager-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Manage portfolios</DialogTitle>
          <DialogDescription>
            Portfolios group your accounts (e.g. "Retirement" vs "Taxable brokerage"). The rollup and growth chart filter by whichever portfolio you pick.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-5 pr-1">
          {/* Create form */}
          <div className="rounded-md border border-border p-4 space-y-3">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Create portfolio</div>
            <div className="grid sm:grid-cols-[2fr_3fr_auto] gap-2">
              <Input
                placeholder="e.g. Retirement"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid="portfolio-new-name"
              />
              <Input
                placeholder="Short description (optional)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                data-testid="portfolio-new-desc"
              />
              <Button onClick={create} disabled={busy || !newName.trim()} data-testid="portfolio-new-save" className="rounded-full">
                <Plus className="w-4 h-4 mr-1.5" /> Add
              </Button>
            </div>
          </div>

          {/* Existing portfolios */}
          <div className="space-y-2">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Portfolios ({portfolios.length})</div>
            {portfolios.length === 0 && (
              <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-md">
                No portfolios yet. Create one above to group your accounts.
              </div>
            )}
            {portfolios.map((p) => (
              <div key={p.id} className="border border-border rounded-md p-3 space-y-2" data-testid={`portfolio-row-${p.id}`}>
                <div className="flex items-center gap-2">
                  {editingId === p.id ? (
                    <>
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="max-w-xs" data-testid={`portfolio-edit-name-${p.id}`} />
                      <Button size="sm" onClick={() => rename(p)} disabled={busy} data-testid={`portfolio-edit-save-${p.id}`}>
                        <Save className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{p.name}</div>
                        {p.description && <div className="text-xs text-muted-foreground">{p.description}</div>}
                      </div>
                      <Badge variant="outline" className="mono text-[10px]">{p.account_count || 0} account(s)</Badge>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(p.id); setEditName(p.name); }} data-testid={`portfolio-edit-${p.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(p)} disabled={busy} data-testid={`portfolio-delete-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Assign accounts */}
          {accounts.length > 0 && (
            <div className="space-y-2">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Assign accounts</div>
              <div className="space-y-1.5">
                {accounts.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2 text-sm" data-testid={`assign-row-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{a.name}</div>
                      <div className="mono text-[10px] text-muted-foreground">{a.type}</div>
                    </div>
                    <select
                      value={a.portfolio_id || ""}
                      onChange={(e) => assignAccount(a.id, e.target.value)}
                      className="h-8 px-2 rounded-md border border-input bg-background text-xs"
                      disabled={busy}
                      data-testid={`assign-select-${a.id}`}
                    >
                      <option value="">Ungrouped</option>
                      {portfolios.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="portfolio-manager-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Paste dialog --------
function PasteDialog({ open, onOpenChange, accountId, onApplied }) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("replace");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setText(""); setMode("replace"); } }, [open]);

  const apply = async () => {
    if (!accountId || !text.trim()) return;
    setSaving(true);
    try {
      const r = await api.post(`/holdings/accounts/${accountId}/paste`, { text, mode });
      const warnings = r.data.warnings || [];
      toast.success(`Parsed ${r.data.parsed} position${r.data.parsed === 1 ? "" : "s"}${warnings.length ? ` (${warnings.length} warning${warnings.length === 1 ? "" : "s"})` : ""}`);
      warnings.slice(0, 3).forEach((w) => toast.warning(w));
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Paste failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="holdings-paste-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Paste positions</DialogTitle>
          <DialogDescription>
            One position per line: <span className="mono text-foreground">TICKER,SHARES,COST_BASIS</span>. Cost basis is total dollars (not per-share). Spaces or tabs also work.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"SCHD,50,4000\nVYM,30,3200\nJEPI 25 1400"}
            rows={10}
            className="mono text-sm"
            data-testid={TEST_IDS.holdingsPasteTextarea}
          />
          <div className="flex items-center gap-2 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="holdings-paste-mode-replace" />
              <span>Replace all positions</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer ml-4">
              <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} data-testid="holdings-paste-mode-merge" />
              <span>Merge with existing</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={apply}
            disabled={!text.trim() || saving}
            data-testid={TEST_IDS.holdingsPasteApply}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ClipboardPaste className="w-4 h-4 mr-1.5" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Push notifications toggle ----------
// Shown in the Holdings header — one-click enable/disable for the current
// browser + a small "test" trigger so users can prove it's working before
// they wait for the next ex-dividend cycle.
function PushToggle() {
  const { supported, permission, subscribed, loading, enable, disable, sendTest } = usePushNotifications();
  if (!supported) return null;
  const blocked = permission === "denied";

  if (blocked) {
    return (
      <div className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-destructive/40 text-destructive inline-flex items-center gap-1.5" title="You've blocked notifications in your browser settings" data-testid="push-blocked">
        <BellOff className="w-3.5 h-3.5" /> Notifications blocked
      </div>
    );
  }

  if (subscribed) {
    return (
      <div className="inline-flex items-center gap-1" data-testid="push-toggle-on">
        <Button variant="outline" size="sm" onClick={sendTest} className="rounded-full" data-testid="push-test">
          <Bell className="w-3.5 h-3.5 mr-1.5 text-primary" /> Test
        </Button>
        <Button variant="ghost" size="sm" onClick={disable} disabled={loading} className="rounded-full text-muted-foreground" data-testid="push-disable">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Off"}
        </Button>
      </div>
    );
  }

  return (
    <Button variant="outline" size="sm" onClick={enable} disabled={loading} className="rounded-full" data-testid="push-enable">
      {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Enabling…</> : <><Bell className="w-3.5 h-3.5 mr-1.5" /> Ex-div alerts</>}
    </Button>
  );
}


// ---------- SnapTrade-based brokerage connect (read-only OAuth) ----------
// Opens SnapTrade's hosted portal in a new tab, then polls /snaptrade/status
// on return to detect the fresh connection and auto-fire the initial sync.
// The user's SnapTrade userSecret is encrypted at rest with Fernet
// (`PLAID_TOKEN_ENC_KEY` env var — reused from the retired Plaid integration).
function BrokerageConnectButton() {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const r = await api.get("/snaptrade/status");
      setStatus(r.data);
    } catch (_) { setStatus({ configured: false }); }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  // On post-portal return we get `?snaptrade=success` — auto-fire a sync.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("snaptrade") !== "success") return;
    (async () => {
      const t = toast.loading("Broker connected — pulling positions (30-90s)…", { id: "snaptrade-post-sync" });
      try {
        await api.post("/snaptrade/sync", {});
        const done = await pollSyncCompletion();
        toast.dismiss(t);
        if (done.ok) {
          toast.success(`Imported ${done.positions} position${done.positions === 1 ? "" : "s"} across ${done.accounts} account${done.accounts === 1 ? "" : "s"}`);
        } else {
          toast.error(done.error, { duration: 10000 });
        }
      } finally {
        window.history.replaceState({}, "", window.location.pathname);
        await refresh();
        window.location.reload();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollSyncCompletion = React.useCallback(async (timeoutMs = 120000) => {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const r = await api.get("/snaptrade/status");
        const s = r.data?.item?.sync_state;
        if (s?.status === "success") return { ok: true, positions: s.positions || 0, accounts: s.accounts || 0 };
        if (s?.status === "error") return { ok: false, error: s.error || "Sync failed" };
      } catch (_) { /* retry */ }
    }
    return { ok: false, error: "Sync is taking longer than expected — try again in a minute." };
  }, []);

  const connect = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/snaptrade/login-link");
      if (!data?.portal_url) throw new Error("Backend returned no portal_url");
      window.location.href = data.portal_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not open SnapTrade portal";
      toast.error(`Connect failed: ${msg}`, { duration: 8000 });
    } finally { setLoading(false); }
  };

  const sync = async () => {
    setLoading(true);
    const t = toast.loading("Refreshing from broker…", { id: "snaptrade-sync" });
    try {
      await api.post("/snaptrade/sync", {});
      const done = await pollSyncCompletion();
      toast.dismiss(t);
      if (done.ok) {
        toast.success(`Refreshed — ${done.positions} position${done.positions === 1 ? "" : "s"} across ${done.accounts} account${done.accounts === 1 ? "" : "s"}`);
        window.location.reload();
      } else {
        toast.error(done.error, { duration: 10000 });
      }
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally { setLoading(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your brokerage? Synced positions will be removed.")) return;
    try {
      await api.delete("/snaptrade/connection");
      toast.success("Disconnected");
      await refresh();
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Disconnect failed");
    }
  };


  const nukeAllHoldings = async () => {
    // Nuclear reset — wipes EVERY row (manual entries INCLUDED). Only use
    // when the app got into a bad state (stale SnapTrade rows, wrong cost
    // basis stuck on manual accounts, individual holdings that won't leave
    // the screen despite disconnect). Requires TWO scary confirms.
    if (!window.confirm("⚠️ NUCLEAR RESET: this deletes EVERY account and position you have — including manual entries you typed by hand. This cannot be undone. Continue?")) return;
    if (!window.confirm("Really? Type-in manual entries are ALSO gone. Last chance.")) return;
    try {
      const r = await api.post("/snaptrade/reset-all-my-holdings", {});
      toast.success(`Wiped ${r.data.holdings_accounts_deleted} accounts + ${r.data.transactions_deleted} transactions. Reloading…`, { duration: 4000 });
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const dumpDebugSnapshot = async () => {
    // Admin helper: pulls the raw SnapTrade positions + balances response
    // and downloads it as JSON. Lets us diagnose "cash is $0 but broker
    // shows $X" or "gain looks inflated" issues in one paste.
    try {
      const r = await api.get("/snaptrade/raw-snapshot");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `snaptrade-raw-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded raw SnapTrade snapshot");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Debug dump failed");
    }
  };


  const syncTransactions = async () => {
    // Pulls dividend/buy/sell history from SnapTrade. Costs 1 of the user's
    // 2-per-month non-admin quota. On 402 we route to /pricing with the
    // upgrade nudge in the toast body.
    const t = toast.loading("Pulling transaction history from broker (30-60s)…", { id: "snaptrade-tx" });
    try {
      const r = await api.post("/snaptrade/transactions/sync", {}, { params: { days: 730 } });
      toast.dismiss(t);
      toast.success(`Synced ${r.data.activities_stored} transactions · ${r.data.quota_used_this_month}/2 used this month`, { duration: 6000 });
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.dismiss(t);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Transaction sync failed";
      if (status === 402) {
        toast.error(detail, {
          action: { label: "Upgrade to Pro", onClick: () => (window.location.href = "/pricing") },
          duration: 10000,
        });
      } else if (status === 409) {
        // Auto-purge already happened server-side. Prompt reconnect.
        toast.error(detail, {
          action: { label: "Reconnect now", onClick: () => window.location.reload() },
          duration: 12000,
        });
      } else {
        toast.error(detail);
      }
    }
  };

  if (!status || !status.configured) return null;

  if (status.connected) {
    const s = status.item?.sync_state;
    const syncBadge = (() => {
      if (!s) return null;
      if (s.status === "pending") return { text: "Syncing…", cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" };
      if (s.status === "error")   return { text: `Sync failed: ${(s.error || "unknown").slice(0, 100)}`, cls: "bg-destructive/10 text-destructive border-destructive/30" };
      if (s.status === "success") return { text: `Last sync: ${s.positions || 0} position${s.positions === 1 ? "" : "s"} · ${s.accounts || 0} account${s.accounts === 1 ? "" : "s"}`, cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" };
      return null;
    })();
    const btn = "rounded-full h-9";
    return (
      <div className="flex flex-col gap-2 lg:items-end" data-testid="plaid-connected">
        <div className="flex flex-wrap gap-2 lg:justify-end">
          <Button size="sm" onClick={sync} disabled={loading} className={btn} data-testid="plaid-sync">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
            Sync brokerage
          </Button>
          <Button variant="outline" size="sm" onClick={connect} disabled={loading} className={btn} data-testid="snaptrade-connect-another">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Connect another brokerage
          </Button>
          <Button variant="outline" size="sm" onClick={syncTransactions} className={btn} data-testid="snaptrade-tx-sync"
            title="Pull dividend + trade history from SnapTrade (limited to 2/month for non-admins)">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync history (2/mo)
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          <Button variant="outline" size="sm" onClick={dumpDebugSnapshot} className={`${btn} text-muted-foreground`} data-testid="snaptrade-debug-dump"
            title="Download the raw SnapTrade positions + balances JSON — great for support tickets when a tile shows a wrong number">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Debug JSON
          </Button>
          <Button variant="outline" size="sm" onClick={disconnect} className={`${btn} text-muted-foreground`} data-testid="plaid-disconnect">
            <Unlink className="w-3.5 h-3.5 mr-1.5" /> Disconnect
          </Button>
          <Button variant="outline" size="sm" onClick={nukeAllHoldings} className={`${btn} text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive`} data-testid="snaptrade-nuke"
            title="Nuclear reset — wipes EVERY account (including manual entries). Use when stale positions won't clear via normal Disconnect.">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Reset everything
          </Button>
        </div>
        {syncBadge && (
          <div className={`mono text-[10px] px-2 py-1 rounded-md border leading-tight w-fit ${syncBadge.cls}`} data-testid="plaid-sync-badge">
            {syncBadge.text}
          </div>
        )}
      </div>
    );
  }
  return (
    <Button size="sm" onClick={connect} disabled={loading} className="rounded-full h-9" data-testid="plaid-connect">
      {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Opening…</> : <><Link2 className="w-3.5 h-3.5 mr-1.5" /> Connect brokerage</>}
    </Button>
  );
}
